import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Card, Descriptions, Input, message, Select, Space, Table } from "antd";
import { PageHeader } from "../../components/PageHeader";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { ApprovalTimeline } from "../../components/ApprovalTimeline";
import { QtyInput } from "../../components/QtyInput";
import { useApi } from "../../helpers/useApi";
import { hasAnyGrant, hasPermission } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { useSitesQuery } from "../../helpers/queries";
import { ReceiveDialog } from "../../components/ReceiveDialog";
import { SiteLabel } from "../../components/SiteLabel";
import type { MRQ, TransferNote, TransferOrder } from "../../api-models";

interface DraftLine {
  mrqLineId: number;
  allocationId: number;
  sourceSiteId: number;
  itemCode: string;
  itemName: string;
  unit?: string;
  outstanding: number;
  qty: number;
}

// A line is only transferable through this screen once it has a SITE_TRANSFER
// FulfillmentAllocation (spec §13) — outstanding is what's left of that allocation, not
// the raw requested qty. Each allocation becomes its own draft row since a line can be
// split across more than one source site.
function siteTransferRows(l: NonNullable<MRQ["lines"]>[number]): DraftLine[] {
  return (l.allocations ?? [])
    .filter((a) => a.sourceType === "SITE_TRANSFER" && a.status !== "CANCELLED" && a.sourceSiteId != null)
    .map((a) => {
      const outstanding = a.qty - a.cancelledQty - a.dispatchedQty;
      return {
        mrqLineId: l.id,
        allocationId: a.id,
        sourceSiteId: a.sourceSiteId!,
        itemCode: l.item?.code ?? String(l.itemId),
        itemName: l.item?.name ?? "",
        unit: l.item?.unit?.abbreviation,
        outstanding,
        qty: outstanding,
      };
    })
    .filter((r) => r.outstanding > 0.0005);
}

export default function TransferDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { request, loading } = useApi();
  const { data: sites = [] } = useSitesQuery();

  const isNew = id === "new";
  const mrqId = searchParams.get("mrqId");
  const [mrq, setMrq] = useState<MRQ | null>(null);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [fromSiteId, setFromSiteId] = useState<number>();
  const [notes, setNotes] = useState("");
  const [t, setT] = useState<TransferOrder | null>(null);
  const [note, setNote] = useState<TransferNote | null>(null);
  const [receiveOpen, setReceiveOpen] = useState(false);

  async function load() {
    if (isNew) return;
    const res = await request<TransferOrder>({ url: `/transfer-orders/${id}` });
    setT(res);
    // The dispatch document, if the source site has already sent the goods — this is
    // what the destination receives against (spec §13).
    const notes = await request<{ data: TransferNote[] }>({ url: "/transfer-notes", params: { transferOrderId: id } });
    const first = (notes.data ?? [])[0];
    setNote(first ? await request<TransferNote>({ url: `/transfer-notes/${first.id}` }) : null);
  }

  useEffect(() => {
    if (isNew && mrqId) {
      request<MRQ>({ url: `/mrqs/${mrqId}` }).then((res) => {
        setMrq(res);
        setLines((res.lines ?? []).flatMap(siteTransferRows));
      });
    } else {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mrqId]);

  const sourceSiteIds = Array.from(new Set(lines.map((l) => l.sourceSiteId)));
  useEffect(() => {
    if (fromSiteId == null && sourceSiteIds.length > 0) setFromSiteId(sourceSiteIds[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines]);

  // Central Procurement creates transfer orders (Transfers.Write requires a global-scope
  // grant, gin.go-style — see transfer_order.go's checkPermissions(..., nil)).
  const canWrite = hasPermission(user, "Transfers.Write", null);
  const canExecuteFrom = hasPermission(user, "Transfers.Execute", t?.fromSiteId);
  const canReceive = hasAnyGrant(user, "MRN.Write");
  const outstandingLines = (note?.lines ?? [])
    .map((l) => ({
      sourceLineId: l.id,
      itemCode: l.item?.code ?? String(l.itemId),
      itemName: l.item?.name ?? "",
      unit: l.item?.unit?.abbreviation,
      outstanding: l.dispatchedQty - l.receivedQty,
    }))
    .filter((l) => l.outstanding > 0.0005);

  async function submit() {
    if (!mrq || !fromSiteId) return;
    const items = lines
      .filter((l) => l.sourceSiteId === fromSiteId && l.qty > 0)
      .map((l) => ({ mrqLineId: l.mrqLineId, allocationId: l.allocationId, qty: l.qty }));
    if (items.length === 0) {
      message.error("Enter a quantity for at least one line.");
      return;
    }
    const created = await request<TransferOrder>({
      method: "POST",
      url: "/transfer-orders",
      data: { fromSiteId, toSiteId: mrq.siteId, notes, lines: items },
    });
    message.success("Transfer order created.");
    navigate(`/transfers/${created.id}`, { replace: true });
  }

  async function act(action: string) {
    await request({ method: "POST", url: `/transfer-orders/${id}/${action}` });
    message.success("Done.");
    load();
  }

  if (isNew) {
    const visibleLines = lines.filter((l) => l.sourceSiteId === fromSiteId);
    return (
      <div>
        <PageHeader
          title="New Transfer"
          subtitle={mrq ? <>{mrq.docNo} — <SiteLabel site={mrq.site} project={mrq.project} /></> : undefined}
          extra={
            <Button type="primary" onClick={submit} loading={loading} disabled={!mrq || !fromSiteId} data-tour="transfer-create">
              Create transfer order
            </Button>
          }
        />
        <Card size="small" style={{ marginBottom: 16 }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="From site">
              <Select
                style={{ width: 220 }}
                options={sourceSiteIds.map((sid) => ({ value: sid, label: sites.find((s) => s.id === sid)?.name ?? sid }))}
                value={fromSiteId}
                onChange={setFromSiteId}
              />
            </Descriptions.Item>
            <Descriptions.Item label="To site"><SiteLabel site={mrq?.site} project={mrq?.project} /></Descriptions.Item>
          </Descriptions>
        </Card>
        <Card size="small" title="Lines" data-tour="transfer-lines">
          <Table
            rowKey="allocationId"
            dataSource={visibleLines}
            pagination={false}
            columns={[
              { title: "Code", dataIndex: "itemCode" },
              { title: "Item", dataIndex: "itemName" },
              { title: "Outstanding", render: (_, r) => `${r.outstanding} ${r.unit ?? ""}` },
              {
                title: "Transfer qty",
                render: (_, r) => (
                  <QtyInput
                    value={r.qty}
                    unit={r.unit}
                    onChange={(v) =>
                      setLines((prev) => prev.map((l) => (l.allocationId === r.allocationId ? { ...l, qty: v ?? 0 } : l)))
                    }
                  />
                ),
              },
            ]}
          />
          <Input.TextArea style={{ marginTop: 12 }} placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </Card>
      </div>
    );
  }

  if (!t) return null;

  return (
    <div>
      <PageHeader
        title={t.docNo}
        extra={
          <Space>
            <span data-tour="doc-status">
              <DocumentStatusTag status={t.status} />
            </span>
            {t.status === "DRAFT" && canWrite && (
              <Button onClick={() => act("submit")} loading={loading} data-tour="transfer-submit">
                Submit for approval
              </Button>
            )}
            {(t.status === "DRAFT" || t.status === "PENDING_APPROVAL") && canWrite && (
              <Button danger onClick={() => act("cancel")} loading={loading}>
                Cancel
              </Button>
            )}
            {t.status === "APPROVED" && canExecuteFrom && (
              <Button onClick={() => act("dispatch")} loading={loading} data-tour="transfer-dispatch">
                Dispatch
              </Button>
            )}
          </Space>
        }
      />
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="From site"><SiteLabel site={t.fromSite} /></Descriptions.Item>
          <Descriptions.Item label="To site"><SiteLabel site={t.toSite} /></Descriptions.Item>
        </Descriptions>
      </Card>
      <Card size="small" title="Lines" style={{ marginBottom: 16 }}>
        <Table
          rowKey="id"
          dataSource={t.lines ?? []}
          pagination={false}
          columns={[
            { title: "Code", render: (_, r) => r.item?.code },
            { title: "Item", render: (_, r) => r.item?.name },
            { title: "Qty", dataIndex: "qty" },
          ]}
        />
      </Card>

      {note && (
        <Card
          size="small"
          title={`Transfer Note ${note.docNo}`}
          style={{ marginBottom: 16 }}
          extra={
            canReceive &&
            outstandingLines.length > 0 && (
              <Button type="primary" size="small" onClick={() => setReceiveOpen(true)}>
                Receive at destination
              </Button>
            )
          }
        >
          <Table
            rowKey="id"
            dataSource={note.lines ?? []}
            pagination={false}
            columns={[
              { title: "Code", render: (_, r) => r.item?.code },
              { title: "Item", render: (_, r) => r.item?.name },
              { title: "Dispatched", dataIndex: "dispatchedQty" },
              { title: "Received", dataIndex: "receivedQty" },
              { title: "Outstanding", render: (_, r) => r.dispatchedQty - r.receivedQty },
            ]}
          />
          {outstandingLines.length > 0 && (
            <small>The outstanding quantity sits in this dispatch&apos;s own in-transit location until the destination receives it.</small>
          )}
        </Card>
      )}

      <ApprovalTimeline docType="TRANSFER_ORDER" docId={Number(id)} refreshKey={t?.status} onActed={load} />

      {note && (
        <ReceiveDialog
          open={receiveOpen}
          onClose={() => {
            setReceiveOpen(false);
            load();
          }}
          sourceType="TRANSFER_NOTE"
          sourceId={note.id}
          lines={outstandingLines}
        />
      )}
    </div>
  );
}
